import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { toast } from '@kognitos/lattice';

import {
  useManagedOAuthReturn,
  type IOAuthConnectionResume,
} from '@/modules/credentials';
import { captureException } from '@/modules/sentry';
import { hasPendingTriggerConnection } from '@/modules/triggers/utils/trigger-connection-intent';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';

import { useRemoteSecretConnectionActions } from './use-remote-secret-connection-actions';

/**
 * Finishes connections whose dialog can't reopen after an OAuth redirect (e.g.
 * inside a trigger or the finance wizard): once the credential is signed in,
 * creates or updates the connection saved before leaving, like BDK's own OAuth
 * flow does. Mount once, app-wide.
 */
export function useConnectOnOAuthReturn() {
  const { t } = useTranslation('integrations');
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);
  const { createConnectionWithRemoteSecret, saveConnectionWithRemoteSecret } =
    useRemoteSecretConnectionActions(organizationId, workspaceId);

  const onResume = useCallback(
    async (resume: IOAuthConnectionResume) => {
      const connect = resume.connectOnReturn;
      if (!connect) return;
      if (resume.error) {
        toast.error(t('integrations.addConnection.oauthError'));
        return;
      }
      const integration = {
        name: resume.integrationName,
        version: resume.integrationVersion,
      };
      const { configMap } = connect;
      // The sign-in's own workspace: the store may not match the URL yet.
      const workspace = {
        organizationId: resume.organizationId,
        workspaceId: resume.workspaceId,
      };
      try {
        if (connect.action === 'create') {
          await createConnectionWithRemoteSecret({
            integration,
            connectionName: connect.connectionName,
            authMethodId: connect.authMethodId,
            credentialId: resume.credentialId,
            labels: connect.labels,
            configMap,
            workspace,
          });
          toast.success(t('integrations.addConnection.success'));
        } else {
          await saveConnectionWithRemoteSecret({
            integration,
            connection: {
              connection_id: connect.connectionId,
              connection_name: connect.connectionName,
              labels: connect.labels,
              authentication: { authentication_id: connect.authMethodId },
            },
            credentialId: resume.credentialId,
            configMap,
            workspace,
          });
          toast.success(t('integrations.editConnection.success'));
        }
      } catch (error) {
        captureException(error as Error, {
          tags: { module: 'integrations', action: 'connectOnOAuthReturn' },
        });
        toast.error(
          t(
            connect.action === 'create'
              ? 'integrations.addConnection.connectOnReturnError'
              : 'integrations.editConnection.error',
          ),
        );
      }
    },
    [t, createConnectionWithRemoteSecret, saveConnectionWithRemoteSecret],
  );

  const handleResume = useCallback(
    (resume: IOAuthConnectionResume) => void onResume(resume),
    [onResume],
  );
  // A trigger dialog reopening on this return reads (and clears) the params.
  const { pathname } = useLocation();
  const keepReturnParams = useCallback(
    () => hasPendingTriggerConnection(pathname),
    [pathname],
  );
  useManagedOAuthReturn({
    source: 'connect',
    onResume: handleResume,
    keepReturnParams,
  });
}
